/**
 * Usage Configuration for Chemicals (Raw Materials)
 * Uses current_unit_quantity for lot selection (whole units — drums/bags/totes).
 * Inserts raw_materials_transactions with unit_quantity + total_quantity (negative).
 * DB triggers handle FIFO reduction and aggregate updates.
 */

import { supabase } from '../../../../../supabaseClient';

export const usageChemicalConfig = {
  type:              'chemicals',
  displayName:       'Chemicals',
  singularName:      'Chemical',
  itemIdField:       'raw_material_id',
  itemNameField:     'name',

  inventoryTable:    'raw_materials_inventory',
  itemsTable:        'raw_materials_inventory_items',
  transactionsTable: 'raw_materials_transactions',

  /**
   * Get available lot items for a chemical at a location.
   * Returns current_quantity alias (= current_unit_quantity) for stepper compatibility.
   */
  getInventoryItems: async (companyId, itemId, locationId) => {
    const { data, error } = await supabase
      .from('raw_materials_inventory_items')
      .select(`
        *,
        raw_materials (
          id, name, sku,
          primary_unit_type,
          primary_unit_quantity,
          primary_unit_measure
        )
      `)
      .eq('company_id', companyId)
      .eq('raw_material_id', itemId)
      .eq('location_id', locationId)
      .eq('is_active', true)
      .gt('current_unit_quantity', 0)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return (data || []).map(item => ({
      ...item,
      current_quantity: item.current_unit_quantity
    }));
  },

  /**
   * Execute usage transactions for selected lots.
   * Quantities stored as negative values in raw_materials_transactions.
   */
  executeUsage: async (companyId, usageData) => {
    const { locationId, usages, notes } = usageData;

    for (const usage of usages) {
      const { itemId, lotId, quantity, lot_number, batch_number, quantityMode } = usage;

      const { data: lotData } = await supabase
        .from('raw_materials_inventory_items')
        .select('raw_material_id, raw_materials(primary_unit_quantity)')
        .eq('id', lotId)
        .single();

      const unitSize = parseFloat(lotData?.raw_materials?.primary_unit_quantity) || 1;
      let unitQty, totalQty;
      if (quantityMode === 'measure') {
        totalQty = parseFloat(parseFloat(quantity).toFixed(4));
        unitQty  = parseFloat((totalQty / unitSize).toFixed(4));
      } else {
        unitQty  = parseInt(quantity, 10);
        totalQty = parseFloat((unitQty * unitSize).toFixed(4));
      }

      const { error: txError } = await supabase
        .from('raw_materials_transactions')
        .insert([{
          company_id:       companyId,
          raw_material_id:  itemId,
          transaction_type: 'usage',
          unit_quantity:    -Math.abs(unitQty),
          total_quantity:   -Math.abs(totalQty),
          location_id:      locationId,
          lot_number:       lot_number,
          batch_number:     batch_number,
          transaction_date: new Date().toISOString(),
          notes:            notes || null
        }]);

      if (txError) throw txError;
    }
  }
};
